'use client';

import { Button } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { Spinner } from '@shared/icons';
import { Address } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from '../../../Toast/Toast';
import { RegistrationAddressFormFields } from '../../../form-controls/Address/RegistrationAddressFormFields';
import { createRegistrationAddressSchema } from '../../../form-controls/Address/schema';
import { useRegistrationAddressFormMode } from '../../../form-controls/Address/useRegistrationAddressFormMode';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { applyAddressSchemaValidationErrors, buildProfileAddressDataPayload, type AddressEditFormData } from '../addressEditSchema';
import { useUpdatePlayerProfile } from '../hooks/useUpdatePlayerProfile';

type AddressEditStepProps = {
  onSave?: (newAddress: string) => void;
  isSheetOpen: boolean;
};

type AddressEditBaseline = {
  address: AddressEditFormData['address'];
  addressLookup: string;
};

function AddressEditStep({ onSave, isSheetOpen }: AddressEditStepProps) {
  const t = useTranslations('form');
  const tAccount = useTranslations('my_account.profile.profile_info');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const addressSchema = useMemo(() => createRegistrationAddressSchema(t), [t]);

  const { skipToStep } = useMultistepForm();
  const form = useFormContext<AddressEditFormData>();

  const { showManualEntry, lookupOpen, setLookupOpen, handleAddressSelect, toggleEntryMode } = useRegistrationAddressFormMode({
    form,
  });

  const watchedHouseNameNumber = form.watch('address.houseNameNumber');
  const watchedStreetName = form.watch('address.streetName');
  const watchedLine2 = form.watch('address.line2');
  const watchedCity = form.watch('address.city');
  const watchedState = form.watch('address.state');
  const watchedPostcode = form.watch('address.postcode');
  const watchedAddressLookup = form.watch('addressLookup');

  const watchedAddress = useMemo(
    () => ({
      houseNameNumber: watchedHouseNameNumber ?? '',
      streetName: watchedStreetName ?? '',
      line2: watchedLine2 ?? '',
      city: watchedCity ?? '',
      state: watchedState ?? '',
      postcode: watchedPostcode ?? '',
      buildingName: '',
      buildingNumber: '',
      street: '',
    }),
    [watchedHouseNameNumber, watchedStreetName, watchedLine2, watchedCity, watchedState, watchedPostcode]
  );

  const baseline = useMemo((): AddressEditBaseline | undefined => {
    if (!isSheetOpen) {
      return;
    }

    return {
      address: { ...form.getValues('address') },
      addressLookup: String(form.getValues('addressLookup') ?? ''),
    };
    // Snapshot when the sheet opens only — do not reset when form state updates.
  }, [isSheetOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- baseline tied to sheet visibility

  const isFormChanged = useMemo(() => {
    if (!baseline) {
      return false;
    }

    const addressChanged =
      watchedAddress.houseNameNumber !== baseline.address.houseNameNumber ||
      watchedAddress.streetName !== baseline.address.streetName ||
      (watchedAddress.line2 ?? '') !== (baseline.address.line2 ?? '') ||
      watchedAddress.city !== baseline.address.city ||
      (watchedAddress.state ?? '') !== (baseline.address.state ?? '') ||
      watchedAddress.postcode !== baseline.address.postcode;

    return addressChanged || String(watchedAddressLookup ?? '') !== baseline.addressLookup;
  }, [baseline, watchedAddress, watchedAddressLookup]);

  const formatFullAddress = () => {
    const address = form.getValues('address');
    const showsStateSelect = getSiteConfig().REGISTRATION.showsStateSelect;
    return [address.houseNameNumber, address.streetName, address.line2, address.city, ...(showsStateSelect ? [address.state] : []), address.postcode].filter(Boolean).join(', ');
  };

  const updateProfileMutation = useUpdatePlayerProfile();

  const handleContinue = async () => {
    if (!showManualEntry) {
      const addressLookupValue = form.getValues('addressLookup');

      if (typeof addressLookupValue !== 'string' || addressLookupValue.trim() === '') {
        form.setError(
          'addressLookup',
          {
            type: 'manual',
            message: t('validation.address.lookup.required'),
          },
          { shouldFocus: false }
        );
        setSubmitAttempted(true);
        return;
      }

      const isFormValid = await form.trigger('address');
      if (!isFormValid) {
        setSubmitAttempted(true);
        return;
      }
    } else {
      setSubmitAttempted(true);
      const address = form.getValues('address');
      const parseResult = addressSchema.safeParse(address);

      if (!applyAddressSchemaValidationErrors(form, parseResult)) {
        return;
      }
    }

    try {
      await updateProfileMutation.mutateAsync({
        addressData: buildProfileAddressDataPayload(form.getValues('address')),
      });

      const formatted = formatFullAddress();
      onSave?.(formatted);
      skipToStep('success');
    } catch {
      toast(tAccount('address_edit.update_error_toast'), { type: 'error', position: 'top-right' });
    }
  };

  const isContinueDisabled = !isFormChanged || updateProfileMutation.isPending;

  const headerHidden = lookupOpen && !showManualEntry;
  const showMovingAbroadLink = getSiteConfig().REGISTRATION.country === 'GB';

  return (
    <div className="w-full flex flex-col flex-1 min-h-0 gap-8 p-6 md:p-8 md:max-w-2xl" data-testid="address-edit-step">
      <div className={`shrink-0 transition-all duration-300 ease-in-out ${headerHidden ? 'max-h-0' : 'max-h-none'}`}>
        <div className={`space-y-2 transition-opacity duration-300 ease-in-out ${headerHidden ? 'opacity-0' : 'opacity-100'}`}>
          <Address size="extra-extra-large" data-testid="address-icon" />
          <div className="space-y-2">
            <h4>{tAccount('address_edit.update_address_title')}</h4>
            {showMovingAbroadLink ? (
              <p className="paragraph-explanatory text-muted-foreground">
                {tAccount('address_edit.outside_country_prefix')}{' '}
                <a
                  href="https://beta-help.lottoland.co.uk/faqs/account/1773335863232/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  data-track-name="Foreign Address"
                >
                  {tAccount('address_edit.outside_country_here')}
                </a>
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-8 align-center flex-1 min-h-0 overflow-y-auto">
        <RegistrationAddressFormFields
          formIdPrefix="address-edit"
          showManualEntry={showManualEntry}
          onLookupOpenChange={setLookupOpen}
          onAddressSelect={handleAddressSelect}
          onToggleEntryMode={toggleEntryMode}
          enterManuallyLabel={tAccount('address_edit.enterManually')}
          switchToSearchLabel={tAccount('address_edit.search')}
          lookupVariant="profile"
          submitAttempted={submitAttempted}
        />
      </div>

      <div className="mt-auto shrink-0" data-track-container="Actions">
        <Button type="button" onClick={handleContinue} disabled={isContinueDisabled} className="w-full" data-track-name="Continue">
          {updateProfileMutation.isPending ? <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} /> : t('buttons.continue')}
        </Button>
      </div>
    </div>
  );
}

export { AddressEditStep };
